/**
 * 顾问陪跑 · 客户详情「发送给客户」（草案 §8 备用通道，PR⑦）。
 *
 * 主通道是企微聊天侧边栏（在和客户的单聊里一键发小程序卡片）；侧边栏不可用时，在这里
 * 「复制短链」或「下载小程序码」发给客户。卡片列表与侧边栏同一份（服务端按真实数据算出）。
 * 小程序码不落存储，只在浏览器里存成文件；短链 30 天有效。
 */
import { useState } from 'react';
import { Alert, Button, Empty, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery } from '@tanstack/react-query';
import { planHeadline, planNotes, sentText } from './today-plan';
import {
  generateAdvisorCardLink,
  listAdvisorCards,
  type AdvisorCardDto,
  type AdvisorCardType,
} from '../../../api/advisor';

const { Text } = Typography;

const TYPE_META: Record<AdvisorCardType, { label: string; color: string }> = {
  order: { label: '订单', color: 'gold' },
  finalize: { label: '定稿确认', color: 'red' },
  reading: { label: '阅读', color: 'green' },
  topic: { label: '话题', color: 'blue' },
};

const errMsg = (e: any, fallback: string) => e?.response?.data?.message ?? fallback;

function downloadPng(base64: string, fileName: string) {
  const a = document.createElement('a');
  a.href = `data:image/png;base64,${base64}`;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function CardsTab({ customerId }: { customerId: string }) {
  const query = useQuery({ queryKey: ['advisor-cards', customerId], queryFn: () => listAdvisorCards(customerId), retry: false });
  const [links, setLinks] = useState<Record<string, string>>({});

  const linkMutation = useMutation({
    mutationFn: (v: { card: AdvisorCardDto; action: 'copy' | 'qrcode' }) => generateAdvisorCardLink(customerId, v.card.key),
    onSuccess: async (data, v) => {
      setLinks((m) => ({ ...m, [v.card.key]: data.shortLink }));
      if (v.action === 'copy') {
        try {
          await navigator.clipboard.writeText(data.shortLink);
          message.success('短链已复制，30 天内有效');
        } catch {
          message.info('浏览器不允许自动复制，请手动复制下方短链');
        }
      } else {
        downloadPng(data.qrcodePngBase64, `${TYPE_META[v.card.type].label}-小程序码.png`);
      }
    },
    onError: (e) => {
      message.error(errMsg(e, '生成失败'));
    },
  });

  if (query.isError) return <Alert type="error" showIcon message={errMsg(query.error, '加载失败')} />;

  const columns: ColumnsType<AdvisorCardDto> = [
    { title: '类型', render: (_, c) => <Tag color={TYPE_META[c.type].color}>{TYPE_META[c.type].label}</Tag> },
    // 给爸妈各做一本时，两本的话题卡标题相同，靠这一列区分
    { title: '哪本书', dataIndex: 'bookLabel' },
    {
      title: '卡片',
      render: (_, c) => (
        <Space direction="vertical" size={0}>
          <Space size={4} wrap>
            <Text>{c.title}</Text>
            {c.recommendation && <Tag color="geekblue">今日推荐 · {c.recommendation.reason}</Tag>}
            {sentText(c) && <Tag color={sentText(c) === '今天已发' ? 'green' : 'default'}>{sentText(c)}</Tag>}
          </Space>
          <Text type="secondary">{c.subtitle}</Text>
          {links[c.key] && <Text copyable>{links[c.key]}</Text>}
        </Space>
      ),
    },
    {
      title: '',
      render: (_, c) => {
        const busy = linkMutation.isPending && linkMutation.variables?.card.key === c.key;
        return (
          <Space>
            <Button size="small" loading={busy && linkMutation.variables?.action === 'copy'} onClick={() => linkMutation.mutate({ card: c, action: 'copy' })}>
              复制短链
            </Button>
            <Button size="small" loading={busy && linkMutation.variables?.action === 'qrcode'} onClick={() => linkMutation.mutate({ card: c, action: 'qrcode' })}>
              下载小程序码
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="平时请在企业微信和客户的单聊里，用聊天侧边栏「顾问助手」一键发卡片；侧边栏用不了时再用这里的短链或小程序码。"
      />
      {(query.data?.plans ?? []).map((plan) => (
        <Alert
          key={plan.workId}
          type={plan.quota > 0 ? 'info' : 'success'}
          showIcon
          message={`${plan.bookLabel}：${planHeadline(plan)}`}
          description={planNotes(plan).map((n) => (
            <div key={n.text}>{n.text}</div>
          ))}
        />
      ))}
      <Table<AdvisorCardDto>
        rowKey="key"
        size="small"
        loading={query.isLoading}
        scroll={{ x: 'max-content' }}
        dataSource={query.data?.cards ?? []}
        columns={columns}
        pagination={false}
        locale={{ emptyText: <Empty description="暂时没有可发的内容（客户还没下单或付款）" /> }}
      />
    </Space>
  );
}
